import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Megaphone, Plus, Power, Trash2, Pencil, X, Check } from 'lucide-react';
import { api } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { MerchantNoticeMarquee } from '@/components/ui/MerchantNoticeMarquee';

interface Notice {
  id: number;
  message: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  isActive: boolean;
  isLive: boolean;
  startsAt: string | null;
  endsAt: string | null;
  merchantIds: number[] | null;
  linkUrl: string | null;
  createdAt: string;
}

/**
 * Hard cap on a marquee message. Must match `MAX_MESSAGE_CHARS` in the backend's
 * merchant-notices service — the server is the real guard, this is what stops a paragraph being
 * pasted in the first place.
 *
 * 200, not the old 500: a marquee is one scrolling line and a merchant reads a few words at a time,
 * so 500 characters scrolls for a long while and gets read by nobody. The four notices live when
 * this was tightened run 106–173 characters, so the real ones already fit.
 */
const NOTICE_MAX_CHARS = 200;

const SEVERITIES = [
  { id: 'INFO', label: 'Info', hint: 'Blue — general announcement' },
  { id: 'WARNING', label: 'Warning', hint: 'Amber — delays, partial disruption' },
  { id: 'CRITICAL', label: 'Critical', hint: 'Red — service interrupted' },
] as const;

/** `<input type="datetime-local">` speaks local wall-clock with no zone; the API speaks ISO. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
const fromLocalInput = (v: string) => (v ? new Date(v).toISOString() : null);

export function MerchantNoticesPanel() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Notice | 'new' | null>(null);

  const { data: notices = [], isLoading } = useQuery({
    queryKey: ['merchant-notices'],
    queryFn: async () => (await api.get<Notice[]>('/merchant-notices')).data,
  });

  const toggle = useMutation({
    mutationFn: async (id: number) => (await api.patch(`/merchant-notices/${id}/toggle`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['merchant-notices'] }); toast.success('Updated'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Update failed'),
  });

  const remove = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/merchant-notices/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['merchant-notices'] }); toast.success('Notice deleted'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Delete failed'),
  });

  const live = notices.filter((n) => n.isLive);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Megaphone size={18} className="text-primary" /> Merchant Notifications
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            A scrolling marquee across the top of every merchant's portal — service interruptions,
            planned downtime, holiday cut-offs.
          </p>
        </div>
        <button onClick={() => setEditing('new')}
          className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-primary/90 shrink-0">
          <Plus size={14} /> New Notice
        </button>
      </div>

      {/* Exactly what a merchant sees right now, rendered by the SAME component their portal uses —
          so what is previewed here cannot drift from what is actually broadcast. */}
      <div>
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-1.5">
          Live preview — what merchants see right now
        </div>
        {live.length > 0
          ? <MerchantNoticeMarquee items={live} />
          : <div className="border border-dashed border-slate-300 rounded-lg px-3 py-2.5 text-sm text-slate-400">
              Nothing is live — merchants see no marquee.
            </div>}
      </div>

      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-3 py-2.5">Message</th>
              <th className="text-left px-3 py-2.5 w-28">Severity</th>
              <th className="text-left px-3 py-2.5 w-44">Window</th>
              <th className="text-left px-3 py-2.5 w-32">Audience</th>
              <th className="text-center px-3 py-2.5 w-24">Status</th>
              <th className="text-right px-3 py-2.5 w-28">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={6} className="text-center py-10 text-slate-400"><Loader2 size={20} className="mx-auto animate-spin" /></td></tr>}
            {!isLoading && notices.length === 0 && (
              <tr><td colSpan={6} className="text-center py-12 text-slate-400">No notices yet — press <strong>New Notice</strong> to broadcast one.</td></tr>
            )}
            {notices.map((n) => (
              <tr key={n.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                <td className="px-3 py-2.5">
                  <div className="text-slate-900">{n.message}</div>
                  {n.linkUrl && <a href={n.linkUrl} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">{n.linkUrl}</a>}
                </td>
                <td className="px-3 py-2.5"><SeverityBadge severity={n.severity} /></td>
                <td className="px-3 py-2.5 text-xs text-slate-600">
                  <div>{n.startsAt ? new Date(n.startsAt).toLocaleString('en-IN') : 'Immediately'}</div>
                  <div className="text-slate-400">→ {n.endsAt ? new Date(n.endsAt).toLocaleString('en-IN') : 'until switched off'}</div>
                </td>
                <td className="px-3 py-2.5 text-xs text-slate-600">
                  {n.merchantIds?.length ? `${n.merchantIds.length} merchant${n.merchantIds.length === 1 ? '' : 's'}` : 'All merchants'}
                </td>
                <td className="px-3 py-2.5 text-center">
                  {n.isLive
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">Live</span>
                    : n.isActive
                      ? <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500" title="On, but outside its start/end window">Scheduled</span>
                      : <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Off</span>}
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => setEditing(n)} title="Edit" className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><Pencil size={14} /></button>
                    <button onClick={() => toggle.mutate(n.id)} title={n.isActive ? 'Switch off' : 'Switch on'}
                      className={`p-1.5 rounded hover:bg-slate-100 ${n.isActive ? 'text-emerald-600' : 'text-slate-400'}`}><Power size={14} /></button>
                    <button
                      onClick={() => { if (confirm('Delete this notice? Merchants will stop seeing it immediately.')) remove.mutate(n.id); }}
                      title="Delete" className="p-1.5 rounded hover:bg-rose-50 text-rose-500"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <NoticeModal
          notice={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['merchant-notices'] }); }}
        />
      )}
    </div>
  );
}

function SeverityBadge({ severity }: { severity: Notice['severity'] }) {
  const cls = severity === 'CRITICAL' ? 'bg-rose-100 text-rose-700'
    : severity === 'WARNING' ? 'bg-amber-100 text-amber-700'
    : 'bg-sky-100 text-sky-700';
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{severity}</span>;
}

function NoticeModal({ notice, onClose, onSaved }: { notice: Notice | null; onClose: () => void; onSaved: () => void }) {
  const [message, setMessage] = useState(notice?.message ?? '');
  const [severity, setSeverity] = useState<Notice['severity']>(notice?.severity ?? 'INFO');
  const [startsAt, setStartsAt] = useState(toLocalInput(notice?.startsAt ?? null));
  const [endsAt, setEndsAt] = useState(toLocalInput(notice?.endsAt ?? null));
  const [linkUrl, setLinkUrl] = useState(notice?.linkUrl ?? '');
  const [audience, setAudience] = useState<'all' | 'some'>(notice?.merchantIds?.length ? 'some' : 'all');
  const [merchantIds, setMerchantIds] = useState<number[]>(notice?.merchantIds ?? []);
  const [search, setSearch] = useState('');

  const { data: merchants = [] } = useQuery({
    queryKey: ['merchants-for-notices'],
    enabled: audience === 'some',
    queryFn: async () => (await api.get('/merchants', { params: { pageSize: 500 } })).data as any,
  });
  const merchantRows: any[] = Array.isArray(merchants) ? merchants : (merchants?.items ?? []);
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return merchantRows;
    return merchantRows.filter((m: any) => `${m.companyName ?? ''} ${m.brandName ?? ''} ${m.id}`.toLowerCase().includes(q));
  }, [merchantRows, search]);

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        message: message.trim(),
        severity,
        startsAt: fromLocalInput(startsAt),
        endsAt: fromLocalInput(endsAt),
        linkUrl: linkUrl.trim() || null,
        merchantIds: audience === 'some' ? merchantIds : null,
      };
      return notice
        ? (await api.patch(`/merchant-notices/${notice.id}`, body)).data
        : (await api.post('/merchant-notices', body)).data;
    },
    onSuccess: () => { toast.success(notice ? 'Notice updated' : 'Notice broadcast'); onSaved(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  const len = message.trim().length;
  const tooLong = len > NOTICE_MAX_CHARS;
  const atLimit = message.length >= NOTICE_MAX_CHARS;
  const canSave = len > 0 && !tooLong && (audience === 'all' || merchantIds.length > 0);

  return (
    <Modal isOpen onClose={onClose} title={notice ? 'Edit Notice' : 'New Merchant Notice'}
      subtitle="Shown as a scrolling marquee at the top of the merchant portal" maxWidth="2xl">
      <div className="space-y-5">
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block flex items-center justify-between">
            <span>Message *</span>
            <span className={tooLong ? 'text-rose-600 font-semibold' : atLimit ? 'text-amber-600 font-semibold' : 'text-slate-400'}>
              {len}/{NOTICE_MAX_CHARS}
            </span>
          </label>
          {/* `maxLength` is the part that actually matters: without it a pasted paragraph was
              accepted into the box and only rejected on Save, so the writer had to find and cut the
              excess themselves. The browser now clips the paste at the boundary. */}
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
            maxLength={NOTICE_MAX_CHARS}
            placeholder="e.g. Service interrupted in Faridabad (121014–121015) due to heavy rain. Pickups resume tomorrow."
            className="input w-full resize-y" />
          {atLimit && (
            <div className="text-[11px] text-amber-700 mt-1">
              Character limit reached — anything further was cut. A marquee is <b>one scrolling line</b>: at this length it
              already takes a while to scroll past, and a merchant sees only a few words at a time. Put the detail behind
              the <b>link</b> below and keep this to the headline.
            </div>
          )}
        </div>

        <div>
          <label className="text-xs font-medium text-slate-500 mb-1.5 block">Severity</label>
          <div className="flex flex-wrap gap-2">
            {SEVERITIES.map((s) => (
              <button key={s.id} onClick={() => setSeverity(s.id)} title={s.hint}
                className={`px-3 py-1.5 rounded-lg text-sm border transition ${
                  severity === s.id ? 'border-primary bg-primary/5 text-primary font-medium' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Show from</label>
            <input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} className="input w-full" />
            <div className="text-[11px] text-slate-400 mt-0.5">Blank = immediately</div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Show until</label>
            <input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} className="input w-full" />
            <div className="text-[11px] text-slate-400 mt-0.5">Blank = until you switch it off</div>
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Link (optional)</label>
          <input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://…" className="input w-full" />
          <div className="text-[11px] text-slate-400 mt-0.5">Adds a "Details" link on the marquee for anything too long to scroll.</div>
        </div>

        <div>
          <label className="text-xs font-medium text-slate-500 mb-1.5 block">Audience</label>
          <div className="flex gap-2 mb-2">
            <button onClick={() => setAudience('all')}
              className={`px-3 py-1.5 rounded-lg text-sm border ${audience === 'all' ? 'border-primary bg-primary/5 text-primary font-medium' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
              All merchants
            </button>
            <button onClick={() => setAudience('some')}
              className={`px-3 py-1.5 rounded-lg text-sm border ${audience === 'some' ? 'border-primary bg-primary/5 text-primary font-medium' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
              Selected merchants ({merchantIds.length})
            </button>
          </div>
          {audience === 'some' && (
            <>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search merchants…" className="input h-8 text-xs w-full mb-1.5" />
              <div className="border border-slate-200 rounded-lg max-h-44 overflow-y-auto">
                {filtered.map((m: any) => (
                  <label key={m.id} className="flex items-center gap-2 px-3 py-1.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 cursor-pointer">
                    <input type="checkbox" checked={merchantIds.includes(m.id)} className="rounded"
                      onChange={() => setMerchantIds((p) => p.includes(m.id) ? p.filter((x) => x !== m.id) : [...p, m.id])} />
                    <span className="text-sm">{m.companyName ?? `#${m.id}`}</span>
                    <span className="text-xs text-slate-400 ml-auto">#{m.id}</span>
                  </label>
                ))}
                {filtered.length === 0 && <div className="px-3 py-4 text-xs text-slate-400 text-center">No merchants match.</div>}
              </div>
            </>
          )}
        </div>

        {message.trim() && (
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-1.5">Preview</div>
            <MerchantNoticeMarquee items={[{ id: -1, message: message.trim(), severity, linkUrl: linkUrl.trim() || null }]} />
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg flex items-center gap-1.5"><X size={14} /> Cancel</button>
          <button onClick={() => save.mutate()} disabled={!canSave || save.isPending}
            className="px-5 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2">
            {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} {notice ? 'Save Changes' : 'Broadcast'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
